/**
 * Domain model for the "Duplicate Lead Review" merge workflow.
 *
 * The backend types leads loosely (`lead_detail: any` across the app), so we
 * describe only the keys this feature reads/writes and keep an index signature
 * for everything else (custom fields, requirement sub-objects, timestamps).
 */

export type LeadId = string;

/** Lightweight reference to a lead used to label where a value/entry came from. */
export interface LeadRef {
  _id: LeadId;
  name: string;
  email?: string;
}

/** A single merged sub-record (note / tag / activity / enquiry). */
export interface LeadNote {
  _id?: string;
  note?: string;
  text?: string;
  description?: string;
  created_at?: string;
  createdAt?: string;
  created_by?: any;
  createdBy?: any;
  [key: string]: any;
}

export interface LeadActivity {
  _id?: string;
  type?: string;
  action?: string;
  message?: string;
  description?: string;
  created_at?: string;
  created_by?: any;
  [key: string]: any;
}

export type LeadTag = string | { _id?: string; tag_name?: string; name?: string; [key: string]: any };

/**
 * Full lead detail as returned by `GET /lead-detail-by-id/:id`.
 * Only the keys used by comparison/merge are named; the rest flow through
 * the index signature so custom fields are never dropped.
 */
export interface DuplicateLead {
  _id: LeadId;

  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  mobile?: string;

  lead_status?: any;
  lead_type?: any;
  lead_source?: any;
  enquiry_type?: any;
  enquiry_details?: any;

  project?: any;
  property?: any;
  listing?: any;
  agent_responsible?: any;

  requirements?: any;
  report_notes?: any;
  notes?: LeadNote[];
  tags?: LeadTag[];
  activities?: LeadActivity[];
  enquiry_history?: LeadActivity[];
  follow_up?: any;
  appointment?: any;

  created_at?: string;
  updated_at?: string;
  duplicate_of?: LeadId | null;

  contactid?: any;
  contact_detail?: any[];

  [key: string]: any;
}

/** Summary row returned by `GET /leads/same_person_prompt/:id`. */
export interface SamePersonLead {
  _id: LeadId;
  first_name?: string;
  last_name?: string;
  email?: string;
  project?: string;
  property?: string;
  agent_responsible?: string;
  [key: string]: any;
}

export interface SamePersonPromptResponse {
  status: boolean;
  data?: SamePersonLead[];
  message?: string;
}

/** How a field is treated by the comparison/merge engine. */
export enum FieldKind {
  /** Single value — copy-or-not, never auto-overwritten. */
  Scalar = 'scalar',
  /** List of entries merged together and de-duplicated (notes, tags, activities). */
  Mergeable = 'mergeable',
}

/**
 * Declarative description of one comparable field. The registry is data, not
 * code paths — new fields (including custom ones) are added by pushing a
 * definition, so the UI and engine stay generic.
 */
export interface FieldDefinition<T = any> {
  key: string;
  label: string;
  kind: FieldKind;
  /** Category header shown in the UI (Details / Requirements / History …). */
  group?: string;
  /** Whether this definition was discovered at runtime (custom field). */
  custom?: boolean;

  /** Read the raw value off a lead. */
  read: (lead: DuplicateLead) => T;

  // --- scalar behaviour ---
  /** Human-readable rendering of a scalar value. */
  display?: (value: T) => string;
  /** Equality between two scalar values (already normalised). */
  equals?: (a: T, b: T) => boolean;

  // --- mergeable behaviour ---
  /** Split a raw value into normalised entries tagged with their source lead. */
  toEntries?: (value: T, source: LeadRef) => MergeEntry[];
  /** Stable de-dupe key for a merge entry. */
  entryKey?: (entry: MergeEntry) => string;

  /**
   * Build the partial payload fragment written to the kept lead.
   * Scalar → `{ [payloadKey]: chosenValue }`.
   * Mergeable → `{ [payloadKey]: mergedRawArrayOrString }`.
   */
  toPayload: (chosen: any, keepLead: DuplicateLead) => Record<string, any>;
}

/** One alternative scalar value offered by a duplicate lead. */
export interface ScalarAlternative {
  value: any;
  display: string;
  source: LeadRef;
  createdAt?: string;
  updatedAt?: string;
}

export type ScalarStatus = 'empty' | 'different' | 'same';

export interface ScalarFieldComparison {
  kind: FieldKind.Scalar;
  key: string;
  label: string;
  group?: string;
  custom: boolean;
  /** Relationship of the kept lead's value to the duplicates. */
  status: ScalarStatus;
  keepValue: any;
  keepDisplay: string;
  keepUpdatedAt?: string;
  /** Distinct alternative values from duplicates (empty when `status === 'same'`). */
  alternatives: ScalarAlternative[];
}

/** A normalised, de-dupable merge entry (note / tag / activity …). */
export interface MergeEntry {
  id: string;
  text: string;
  createdAt?: string;
  createdBy?: string;
  source: LeadRef;
  /** Original object, re-serialised when building the payload. */
  raw: any;
}

export interface MergeableFieldComparison {
  kind: FieldKind.Mergeable;
  key: string;
  label: string;
  group?: string;
  custom: boolean;
  /** Entries already on the kept lead (context only, not selectable). */
  existing: MergeEntry[];
  /** New entries from duplicates, de-duped against `existing` and each other. */
  candidates: MergeEntry[];
}

export type FieldComparison = ScalarFieldComparison | MergeableFieldComparison;

/** One lead's value for one row of the compare grid. */
export interface ComparisonCell {
  display: string;
  /** No value on this lead — rendered as a dash + warning icon. */
  blank: boolean;
  /** This cell's value differs from the kept (recommended) lead's value. */
  different: boolean;
}

/**
 * A row of the compare grid: one field, one cell per candidate lead. Unlike
 * `scalars`/`mergeables` (which only carry what can be COPIED), rows describe
 * every field that any lead holds — including values that all leads agree on.
 */
export interface ComparisonRow {
  key: string;
  label: string;
  kind: FieldKind;
  /** At least two leads hold a different value for this field. */
  different: boolean;
  /** leadId -> cell */
  cells: Record<LeadId, ComparisonCell>;
}

export interface LeadComparisonResult {
  keepLeadId: LeadId;
  /** Everything the grid renders (all leads, all fields with any value). */
  rows: ComparisonRow[];
  scalars: ScalarFieldComparison[];
  mergeables: MergeableFieldComparison[];
  /** Copyable rows = scalars needing action + mergeables with candidates. */
  totalItems: number;
}

// --- selection / merge output --------------------------------------------

export interface ScalarSelection {
  selected: boolean;
  /** Chosen value (when several alternatives exist the user picks one). */
  value: any;
  sourceId: LeadId;
}

/**
 * What happens to a note-style field (notes / report notes / tags / history)
 * when its entries are copied across:
 * - `merge`     — keep what the retained lead already has and append the picks
 * - `overwrite` — replace the retained lead's entries with the picks
 */
export type EntryMergeMode = 'merge' | 'overwrite';

export interface MergeAuditTrail {
  merged_by?: string;
  merged_at?: string;
  keep_lead_id: LeadId;
  source_lead_ids: LeadId[];
  copied_fields: { key: string; label: string; from: LeadId; value: any }[];
  replaced_fields: { key: string; label: string; from: LeadId; old_value: any; new_value: any }[];
  merged_entries: { key: string; label: string; count: number; mode: EntryMergeMode }[];
  duplicate_relationships: { lead_id: LeadId; duplicate_of: LeadId }[];
}

export interface DuplicateLeadUpdate {
  leadId: LeadId;
  update: Record<string, any>;
}

/** Everything needed to execute the merge — built pure, executed by the service. */
export interface MergePayload {
  keepLeadId: LeadId;
  /** Just the selected changes — used for the audit trail. */
  keepUpdate: Record<string, any>;
  /**
   * Full `PUT /update-lead/:id` body for the kept lead: the whole lead object
   * with the approved changes applied. Mirrors how the edit-lead form saves the
   * complete record, so the backend never sees a partial document.
   */
  keepPayload: Record<string, any>;
  /** Full-object updates for each remaining duplicate (flagged as duplicate). */
  duplicateUpdates: DuplicateLeadUpdate[];
  audit: MergeAuditTrail;
}

export interface MergeResult {
  keepLeadId: LeadId;
  duplicateLeadIds: LeadId[];
  audit: MergeAuditTrail;
}

export interface SkipResult {
  keepLeadId: LeadId | null;
  duplicateLeadIds: LeadId[];
}

/** Status name used to flag a lead as a duplicate (matches the leads list). */
export const DUPLICATE_STATUS_NAME = 'Duplicate';
